import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '../api/auth';
import logo from '../assets/sgokit.svg';
import { Arrow } from '../platform/icons';

/**
 * The platform landing's conversion endpoint, shown on sgokit.test (where the
 * branding lookup returns no tenant name) and at /signup. Creating the org sends
 * the first magic-link email, so success drops into a "check your email" state.
 * Styled in the SGOKit platform identity (sgokit.css, scoped under .sgk-site).
 */
const SignUp: React.FC = () => {
  const [organizationName, setOrganizationName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<{ subdomain: string } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authApi.signup({
        organization_name: organizationName,
        subdomain: subdomain || undefined,
        email,
      });
      setDone({ subdomain: res.subdomain });
    } catch (err: any) {
      const data = err.response?.data;
      // DRF returns {field: [msg]} or {non_field_errors: [...]}; surface the first.
      const first =
        data?.subdomain?.[0] ||
        data?.organization_name?.[0] ||
        data?.email?.[0] ||
        data?.non_field_errors?.[0] ||
        data?.detail ||
        'Something went wrong. Please try again.';
      setError(first);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sgk-site">
      <div className="sgk-auth">
        <aside className="sgk-auth__aside">
          <Link to="/" className="sgk-brand" data-testid="signup-brand-link">
            <img className="sgk-brand__logo" src={logo} alt="" />
            <span className="sgk-brand__name">SGO<b>Kit</b></span>
          </Link>

          <div className="sgk-auth__pitch">
            <h2>Stand up your SGO before the <em>credit lands.</em></h2>
            <p>Create your organization in minutes and start taking donations today —
              the whole toolkit is ready when you are.</p>
            <ul>
              <li>Donations via Stripe Connect, straight to your account</li>
              <li>A public state footprint donors can read</li>
              <li>No cut of your donations — fees pass through</li>
            </ul>
          </div>

          <p className="sgk-hero__note" style={{ position: 'relative' }}>
            Section 25F · federal credit up to <b>$1,700</b> · begins 2027
          </p>
        </aside>

        <div className="sgk-auth__main">
          {done ? (
            <div className="sgk-form" style={{ textAlign: 'center' }}>
              <div className="sgk-module__icon" style={{ width: 56, height: 56, borderRadius: 14, margin: '0 auto 20px' }}>
                <Arrow size={26} />
              </div>
              <h1 data-testid="signup-success-heading">Check your email</h1>
              <p className="sgk-form__sub" data-testid="signup-success-message">
                We sent a sign-in link to <strong>{email}</strong>. Open it to finish
                setting up <strong>{done.subdomain}.sgokit.test</strong>.
              </p>
              <Link to="/" className="sgk-authback" data-testid="signup-back-home-link">← Back to home</Link>
            </div>
          ) : (
            <form className="sgk-form" onSubmit={submit} data-testid="signup-form">
              <h1>Start an SGO</h1>
              <p className="sgk-form__sub">Create your organization and we&apos;ll email you a sign-in link.</p>

              {error && (
                <div className="sgk-alert sgk-alert--error" data-testid="signup-error-message">{error}</div>
              )}

              <div className="sgk-field">
                <label htmlFor="su-org">Organization name</label>
                <input id="su-org" className="sgk-input" type="text" required
                  value={organizationName} onChange={(e) => setOrganizationName(e.target.value)}
                  placeholder="Acme Scholarships" data-testid="signup-organization-name-input" />
              </div>

              <div className="sgk-field">
                <label htmlFor="su-sub">Subdomain <span className="opt">(optional)</span></label>
                <div className="sgk-inputgroup">
                  <input id="su-sub" className="sgk-input" type="text"
                    value={subdomain} onChange={(e) => setSubdomain(e.target.value)}
                    placeholder="acme" data-testid="signup-subdomain-input" />
                  <span className="sgk-inputgroup__suffix">.sgokit.test</span>
                </div>
                <p className="sgk-field__hint">Leave blank to derive it from your organization name.</p>
              </div>

              <div className="sgk-field">
                <label htmlFor="su-email">Your email</label>
                <input id="su-email" className="sgk-input" type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@acme.org" data-testid="signup-email-input" />
              </div>

              <button type="submit" className="sgk-btn sgk-btn--gold" disabled={loading}
                data-testid="signup-submit-button">
                {loading ? 'Creating…' : <>Create organization <Arrow /></>}
              </button>

              <Link to="/login" className="sgk-authback" data-testid="signup-signin-link">
                Already have an organization? Sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignUp;
